import { useEffect, useRef } from "react";
import { Grid, MultiSelect, Select } from "@mantine/core";
import { SubDenialReason } from "@/__generated__/graphql";
import { Condition } from "shared/src/data/medication-condition-pairs";
import {
  shouldShowSubDenialReasonField,
  getSubDenialReasonOptions,
} from "shared/src/utils/subDenialReasonUtils";

import styles from "./initial-referral-page.module.scss";

interface SubDenialReasonFieldProps {
  condition: string;
  denialReason: string;
  denialReasonFieldShown: boolean;
  subDenialReasons: SubDenialReason[];
  disabled: boolean;
  onChange: (values: SubDenialReason[]) => void;
  onClear: () => void;
  error?: React.ReactNode;
}

export function SubDenialReasonField({
  condition,
  denialReason,
  denialReasonFieldShown,
  subDenialReasons,
  disabled,
  onChange,
  onClear,
  error,
}: SubDenialReasonFieldProps) {
  const shouldShow = shouldShowSubDenialReasonField(condition, denialReason);
  const prevShouldShowRef = useRef(shouldShow);
  const onClearRef = useRef(onClear);
  onClearRef.current = onClear;

  useEffect(() => {
    if (prevShouldShowRef.current && !shouldShow) {
      onClearRef.current();
    }
    prevShouldShowRef.current = shouldShow;
  }, [shouldShow]);

  if (!shouldShow) {
    return null;
  }

  const options = getSubDenialReasonOptions(condition);
  const isMultiSelect = condition === Condition.CSID;
  const labelBase = denialReasonFieldShown
    ? "Sub denial reason"
    : "Denial reason";
  const label = isMultiSelect ? `${labelBase}s` : labelBase;

  if (isMultiSelect) {
    return (
      <Grid mt="md">
        <Grid.Col>
          <MultiSelect
            label={label}
            placeholder={
              subDenialReasons.length > 0 ? "" : "Select all that apply"
            }
            data={options}
            disabled={disabled}
            searchable
            className={styles.multiSelectWithoutChevron}
            value={subDenialReasons}
            onChange={(values) => onChange(values as SubDenialReason[])}
            error={error}
            data-testid="sub-denial-reason-input"
          />
        </Grid.Col>
      </Grid>
    );
  }

  return (
    <Grid mt="md">
      <Grid.Col>
        <Select
          label={label}
          placeholder="Select one"
          data={options}
          disabled={disabled}
          searchable
          value={subDenialReasons[0] ?? null}
          onChange={(value) =>
            onChange(value ? [value as SubDenialReason] : [])
          }
          error={error}
          data-testid="sub-denial-reason-input"
        />
      </Grid.Col>
    </Grid>
  );
}
